import { createReducer } from 'src/helpers/reducer';
import { isEmpty } from 'lodash';
import { t } from 'src/i18n';
import { IChangePropertyPayload } from '@dash/editForm';
import { Attribute, FieldTypes, ILoadFormDataSuccessPayload, ITrustedTalentApplicationState } from './types';
import actionsTypes from './actionTypes';

const validate = (attr: Attribute, value: FieldTypes) => {
  let errorMessage = '';

  if (attr === 'trusted_by_organization_id') {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t('dash.applicants.upload_trusted_talent_modal.organization_label'),
      });
    }
  }

  if (attr === 'csv_file') {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t('dash.applicants.upload_trusted_talent_modal.applicant_file'),
      });
    }
  }

  return errorMessage;
};

const getInitialField = (fieldName: Attribute, value = null) => ({
  name: fieldName,
  value,
  touched: false,
  error: validate(fieldName, value || ''),
});

const initialEditForm = {
  trusted_by_organization_id: getInitialField('trusted_by_organization_id'),
  csv_file: getInitialField('csv_file'),
};

const initState: ITrustedTalentApplicationState = {
  isFormDataLoading: false,
  isTemplateLoading: false,
  isUploadLoading: false,
  isUploadSuccessful: false,
  formData: undefined,
  editForm: initialEditForm,
};

function onChangeProperty(
  state: ITrustedTalentApplicationState,
  { payload }: { payload: IChangePropertyPayload<Attribute, FieldTypes> },
): ITrustedTalentApplicationState {
  const { attr, value } = payload;
  const { editForm } = state;

  const getValue = (val: FieldTypes, attribute: Attribute) => ({
    value: val,
    error: validate(attribute, val),
    name: attribute,
    touched: true,
  });

  return {
    ...state,
    editForm: {
      ...editForm,
      [attr]: getValue(value, attr),
    },
  };
}

function onLoadFormData(state: ITrustedTalentApplicationState): ITrustedTalentApplicationState {
  return {
    ...state,
    isFormDataLoading: true,
  };
}

function onLoadFormDataSuccess(
  state: ITrustedTalentApplicationState,
  { payload }: { payload: ILoadFormDataSuccessPayload },
): ITrustedTalentApplicationState {
  const { data } = payload;

  return {
    ...state,
    isFormDataLoading: false,
    formData: data,
  };
}

function onLoadFormDataError(state: ITrustedTalentApplicationState): ITrustedTalentApplicationState {
  return {
    ...state,
    isFormDataLoading: false,
  };
}

function onLoadTemplate(state: ITrustedTalentApplicationState): ITrustedTalentApplicationState {
  return {
    ...state,
    isTemplateLoading: true,
  };
}

function onLoadTemplateSuccess(state: ITrustedTalentApplicationState): ITrustedTalentApplicationState {
  return {
    ...state,
    isTemplateLoading: false,
  };
}

function onLoadTemplateError(state: ITrustedTalentApplicationState): ITrustedTalentApplicationState {
  return {
    ...state,
    isTemplateLoading: false,
  };
}

function onUploadTrustedTalent(state: ITrustedTalentApplicationState): ITrustedTalentApplicationState {
  return {
    ...state,
    isUploadLoading: true,
  };
}

function onUploadTrustedTalentSuccess(state: ITrustedTalentApplicationState): ITrustedTalentApplicationState {
  return {
    ...state,
    isUploadLoading: false,
    isUploadSuccessful: true,
  };
}

function onUploadTrustedTalentError(state: ITrustedTalentApplicationState): ITrustedTalentApplicationState {
  return {
    ...state,
    isUploadLoading: false,
    isUploadSuccessful: false,
  };
}

function onClearState(state: ITrustedTalentApplicationState): ITrustedTalentApplicationState {
  return {
    ...state,
    isFormDataLoading: false,
    isTemplateLoading: false,
    isUploadLoading: false,
    isUploadSuccessful: false,
    editForm: initialEditForm,
  };
}

export default createReducer(initState, {
  [actionsTypes.LOAD_FORM_DATA]: onLoadFormData,
  [actionsTypes.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [actionsTypes.LOAD_FORM_DATA_ERROR]: onLoadFormDataError,
  [actionsTypes.CHANGE_PROPERTY]: onChangeProperty,
  [actionsTypes.LOAD_TEMPLATE]: onLoadTemplate,
  [actionsTypes.LOAD_TEMPLATE_SUCCESS]: onLoadTemplateSuccess,
  [actionsTypes.LOAD_TEMPLATE_ERROR]: onLoadTemplateError,
  [actionsTypes.UPLOAD_TRUSTED_TALENT]: onUploadTrustedTalent,
  [actionsTypes.UPLOAD_TRUSTED_TALENT_SUCCESS]: onUploadTrustedTalentSuccess,
  [actionsTypes.UPLOAD_TRUSTED_TALENT_ERROR]: onUploadTrustedTalentError,
  [actionsTypes.CLEAR_STATE]: onClearState,
});
